import React from 'react';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface TooltipProps {
  onClose?(): void;
  title: string | React.ReactNode;
  content?: string | React.ReactNode;
  image?: string;
  extra?: string | React.ReactNode;
  arrow: 'left' | 'right' | 'top' | 'bottom' | 'center' | 'none';
  show: boolean;
  top?: number | string;
  left?: number | string;
  right?: number | string;
  bottom?: number | string;
  transform?: string;
  tooltipColor?: string;
  arrowPositionTop?: string;
  tooltipWidth?: number;
}

export default class Tooltip extends React.Component<TooltipProps> {
  static defaultProps = {
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
  };

  render() {
    const {
      title,
      content,
      extra,
      image,
      arrow,
      show,
      onClose,
      top,
      left,
      right,
      bottom,
      transform,
      tooltipColor,
      arrowPositionTop,
      tooltipWidth,
    } = this.props;

    if (!show) return null;

    return (
      <div
        className={classnames('tooltip-container', `animation-${arrow}`)}
        style={{ top, left, right, bottom, transform }}
      >
        <div className={classnames('wrapper', `arrow-${arrow}`)}>
          <strong>{title}</strong>

          {!!content && <p>{content}</p>}

          {!!image && <img className="image" src={image} />}

          {extra}

          {!!onClose && (
            <div className="close" onClick={onClose}>
              <FontAwesomeIcon icon="times" />
            </div>
          )}
        </div>
        <style jsx>{`
          .tooltip-container {
            background: ${tooltipColor ?? '#008eff'};
            color: white;
            position: absolute;
            width: ${tooltipWidth ?? 224}px;
            z-index: 1000;
            border-radius: 8px;
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
            backface-visibility: hidden;
          }

          .tooltip-container strong {
            font-size: 16px;
            margin-bottom: 12px;
            padding-right: 20px;
            display: block;
          }

          .tooltip-container p {
            margin: 0;
            font-size: 14px;
            line-height: 20px;
          }

          .tooltip-container :global(a) {
            color: white !important;
            font-weight: normal !important;
            text-decoration: underline !important;
          }

          .tooltip-container :global(a):hover {
            opacity: 0.6;
          }

          .tooltip-container .image,
          .tooltip-container :global(img) {
            display: block;
            margin: 12px 0;
            width: 100%;
            max-width: 100%;
            height: auto;
            border-radius: 4px;
          }

          .tooltip-container .wrapper {
            padding: 12px;
            position: relative;
          }

          .tooltip-container .wrapper .close {
            position: absolute;
            top: 16px;
            right: 16px;
            font-size: 16px;
            opacity: 0.5;
            cursor: pointer;
            transition: all 300ms;
          }

          .tooltip-container .wrapper .close:hover {
            opacity: 1;
          }

          .tooltip-container .wrapper .close:active {
            opacity: 0.8;
          }

          .tooltip-container .wrapper:before {
            content: '';
            width: 0;
            height: 0;
            position: absolute;
          }

          .tooltip-container .wrapper.arrow-bottom:before {
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 8px solid ${tooltipColor ?? '#008eff'};
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
          }

          .tooltip-container .wrapper.arrow-top:before {
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-bottom: 8px solid ${tooltipColor ?? '#008eff'};

            top: -8px;
            left: 50%;
            transform: translateX(-50%);
          }

          .tooltip-container .wrapper.arrow-left:before {
            border-top: 8px solid transparent;
            border-bottom: 8px solid transparent;
            border-right: 8px solid ${tooltipColor ?? '#008eff'};

            top: ${arrowPositionTop ?? '50%'};
            left: -8px;
            transform: translateY(-50%);
          }

          .tooltip-container .wrapper.arrow-right:before {
            border-top: 8px solid transparent;
            border-bottom: 8px solid transparent;
            border-left: 8px solid ${tooltipColor ?? '#008eff'};

            top: ${arrowPositionTop ?? '50%'};
            right: -8px;
            transform: translateY(-50%);
          }

          .tooltip-container.animation-bottom {
            margin-top: -12px;
            transform: translate(-50%, -100%);
            transform-origin: center bottom;
            animation: ScaleOpeningBottom 300ms;
          }

          .tooltip-container.animation-top {
            margin-bottom: -12px;
            transform: translate(-50%, 100%);
            transform-origin: center top;
            animation: ScaleOpeningTop 300ms;
          }

          .tooltip-container.animation-left {
            margin-right: -12px;
            transform: translate(100%, -50%);
            transform-origin: center left;
            animation: ScaleOpeningLeft 300ms;
          }

          .tooltip-container.animation-right {
            margin-left: -12px;
            transform: translate(-100%, -50%);
            transform-origin: center right;
            animation: ScaleOpeningRight 300ms;
          }

          .tooltip-container.animation-center {
            transform: translate(-50%, -50%);
            transform-origin: center center;
            animation: ScaleOpeningCenter 300ms;
          }

          .tooltip-container.animation-none {
            transform-origin: center center;
            animation: ScaleOpeningNone 300ms;
          }

          @keyframes ScaleOpeningBottom {
            from {
              transform: translate(-50%, -100%) scale(0.5);
            }
            to {
              transform: translate(-50%, -100%) scale(1);
            }
          }
          @keyframes ScaleOpeningTop {
            from {
              transform: translate(-50%, 100%) scale(0.5);
            }
            to {
              transform: translate(-50%, 100%) scale(1);
            }
          }
          @keyframes ScaleOpeningLeft {
            from {
              transform: translate(100%, -50%) scale(0.5);
            }
            to {
              transform: translate(100%, -50%) scale(1);
            }
          }
          @keyframes ScaleOpeningRight {
            from {
              transform: translate(-100%, -50%) scale(0.5);
            }
            to {
              transform: translate(-100%, -50%) scale(1);
            }
          }
          @keyframes ScaleOpeningCenter {
            from {
              transform: translate(-50%, -50%) scale(0.5);
            }
            to {
              transform: translate(-50%, -50%) scale(1);
            }
          }
          @keyframes ScaleOpeningNone {
            from {
              transform: scale(0.5);
            }
            to {
              transform: scale(1);
            }
          }
        `}</style>
      </div>
    );
  }
}
